import 'bootstrap/dist/css/bootstrap.min.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import VerifiedRoute from './components/VerifiedRoute';
import Navigation from './components/Navigation';
import Login from './components/Login';
import Signup from './components/Signup';
import EmailVerification from './components/EmailVerification';
import EmailSent from './components/EmailSent';
import AdminDashboard from './components/Admin/AdminDashboard';
import AdminSettings from './components/Admin/AdminSettings';
import InventoryManagement from './components/Admin/InventoryManagement';
import Assignment from './components/Admin/Assignment';
import ManualRegistrationPage from './components/Admin/ManualRegistrationPage';
import RegistrationDetailsPage from './components/Admin/RegistrationDetailsPage';
import Reports from './components/Admin/Reports';
import SetupAdmin from './components/Admin/SetupAdmin';
import AdminRoute from './components/Admin/AdminRoute';
import UserManagement from './components/Admin/UserManagement';
import CamperList from './components/Admin/CamperManagement/CamperList';
import CamperEditPage from './components/Admin/CamperManagement/CamperEditPage';
import MergeCamperRecordsPage from './components/Admin/CamperManagement/MergeCamperRecordsPage';
import DevelopmentTools from './components/Admin/DevelopmentTools';
import Registration from './components/registration';
import RegistrationAvailabilityRoute from './components/RegistrationAvailabilityRoute';
import AuthLogs from './components/Admin/AuthLogs';
import AGMReport from './components/Admin/Reports/AGMReport';
import PaymentPage from './components/Payment/PaymentPage';
import PaymentSuccess from './components/Payment/PaymentSuccess';
import CamperInvoicePage from './components/Payment/CamperInvoicePage';
import RefundTool from './components/Admin/RefundTool';
import PaymentLogs from './components/Admin/PaymentLogs';
import RegistrationSuccess from './components/RegistrationSuccess';
import './App.css';
import './styles/mobileCamper.css';

const AppContent: React.FC = () => {
  const location = useLocation();
  const hideNavigation = ['/signup', '/login', '/pay', '/invoice'].some(path => location.pathname.startsWith(path));

  return (
    <div className="app-container">
      {!hideNavigation && <Navigation />}
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/email-sent" element={<EmailSent />} />
        <Route path="/invoice/:registrationId" element={<CamperInvoicePage />} />
        <Route path="/pay/:registrationId" element={<PaymentPage />} />
        <Route path="/pay/success" element={<PaymentSuccess />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        
        {/* Admin routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/setup" element={<ProtectedRoute><SetupAdmin /></ProtectedRoute>} />
        <Route path="/admin/campers" element={<AdminRoute><CamperList /></AdminRoute>} />
        <Route path="/admin/campers/:camperId" element={<AdminRoute><CamperEditPage /></AdminRoute>} />
        <Route path="/admin/campers/merge-records" element={<AdminRoute><MergeCamperRecordsPage /></AdminRoute>} />
        <Route path="/admin/annual-settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/admin/inventory" element={<AdminRoute><InventoryManagement /></AdminRoute>} />
        <Route path="/admin/assignments" element={<AdminRoute><Assignment /></AdminRoute>} />
        <Route path="/admin/assignments/manual-registration" element={<AdminRoute><ManualRegistrationPage /></AdminRoute>} />
        <Route path="/admin/registration-details/:id" element={<AdminRoute><RegistrationDetailsPage /></AdminRoute>} />
        <Route path="/admin/reports" element={<AdminRoute><Reports /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
        <Route path="/admin/dev-tools" element={<AdminRoute><DevelopmentTools /></AdminRoute>} />
        <Route path="/admin/auth-logs" element={
          <AdminRoute>
            <AuthLogs />
          </AdminRoute>
        } />
        <Route path="/admin/agm-report" element={<AdminRoute><AGMReport /></AdminRoute>} />
        <Route path="/admin/refunds" element={<AdminRoute><RefundTool /></AdminRoute>} />
        <Route path="/admin/payment-logs" element={<AdminRoute><PaymentLogs /></AdminRoute>} />

        {/* Protected routes that require email verification */}
        <Route path="/" element={
          <VerifiedRoute>
            <Navigate to="/register" replace />
          </VerifiedRoute>
        } />
        
        {/* Registration flow - requires verification and registration availability */}
        <Route path="/register/*" element={
          <VerifiedRoute>
            <RegistrationAvailabilityRoute>
              <Routes>
                <Route path="/" element={<Registration />} />
                <Route path="/wizard" element={<Registration />} />
              </Routes>
            </RegistrationAvailabilityRoute>
          </VerifiedRoute>
        } />

        {/* Catch all route - redirect to login for unauthenticated users */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
